import cn from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';

import styles from './styles.module.scss';

const SubMarketNeighborhoodListItem = ({
  subMarket,
  onSubMarketChange,
  onNeighborhoodCheck,
  selectedNeighborhoods,
  isChecked,
}) => {
  const [collapsedSubMarkets, setCollapsedSubMarkets] = useState([]);
  const subMarketCheckboxRef = useRef();

  useEffect(() => {
    if (
      subMarket.neighborhoods.filter(
        ({ visibility }) => visibility === 'VISIBLE'
      ).length === 0
    )
      return;

    const selectedNeighborhoodsInSubMarket = subMarket.neighborhoods.filter(
      (nhood) => selectedNeighborhoods.map((n) => n.id).includes(nhood.id)
    );
    if (isChecked) {
      // all neighborhoods selected or submarket selected
      subMarketCheckboxRef.current.checked = true;
      subMarketCheckboxRef.current.indeterminate = false;
    } else if (selectedNeighborhoodsInSubMarket.length > 0) {
      // some neighborhoods selected
      subMarketCheckboxRef.current.indeterminate = true;
      subMarketCheckboxRef.current.checked = false;
    } else {
      // no neighborhoods selected
      subMarketCheckboxRef.current.checked = false;
      subMarketCheckboxRef.current.indeterminate = false;
    }
  }, [selectedNeighborhoods, subMarket.neighborhoods, isChecked]);

  function toggleCollapsedSubMarket(_subMarket) {
    if (collapsedSubMarkets.includes(_subMarket)) {
      setCollapsedSubMarkets(
        collapsedSubMarkets.filter((c) => c !== _subMarket)
      );
    } else {
      setCollapsedSubMarkets([...collapsedSubMarkets, _subMarket]);
    }
  }

  if (
    // eslint-disable-next-line no-underscore-dangle
    subMarket.__typename !== 'Submarket' &&
    subMarket.neighborhoods.length === 0
  )
    return null;

  const visibleNeighborhoods = subMarket.neighborhoods.filter(
    ({ visibility }) => visibility === 'VISIBLE'
  );

  return (
    <React.Fragment key={`city-${subMarket.id}-${subMarket.name}`}>
      <div className={styles.cityCheckbox}>
        <label htmlFor={`toggle-city-${subMarket.id}`}>
          <input
            type="checkbox"
            id={`toggle-city-${subMarket.id}`}
            name="cities"
            value={subMarket.name}
            checked={isChecked}
            onChange={(e) => onSubMarketChange(subMarket, e.target.checked)}
            ref={subMarketCheckboxRef}
          />
          {subMarket.name}
        </label>

        {visibleNeighborhoods.length > 0 && (
          <span
            className={styles.collapse}
            role="button"
            tabIndex={0}
            onKeyPress={({ key }) => {
              if (key === 'Enter') toggleCollapsedSubMarket(subMarket.name);
            }}
            onClick={() => toggleCollapsedSubMarket(subMarket.name)}
          >
            <i
              className={`icon icon-chevron-${
                collapsedSubMarkets.includes(subMarket.name) ? 'down' : 'up'
              }`}
            />
          </span>
        )}
      </div>

      {visibleNeighborhoods.length > 0 && (
        <div
          className={cn(styles.nhoodCheckboxes, {
            [styles.hide]: collapsedSubMarkets.includes(subMarket.name),
          })}
        >
          {subMarket.neighborhoods
            .filter(({ visibility }) => visibility === 'VISIBLE')
            .map((neighborhood) => (
              <label
                key={`nhood-${neighborhood.id}-${neighborhood.name}`}
                htmlFor={`toggle-nhood-${neighborhood.id}`}
                className={styles.checkbox}
              >
                <input
                  type="checkbox"
                  id={`toggle-nhood-${neighborhood.id}`}
                  name="neighborhoods"
                  value={neighborhood.name}
                  checked={
                    isChecked ||
                    selectedNeighborhoods.some((n) => n.id === neighborhood.id)
                  }
                  onChange={(e) =>
                    onNeighborhoodCheck(
                      e.target.checked,
                      neighborhood,
                      subMarket
                    )
                  }
                />
                {neighborhood.name}
                {neighborhood.yearlyPricePerFoot && (
                  <abbr>
                    {' $'}
                    {neighborhood.yearlyPricePerFoot > 60 && '$'}
                    {neighborhood.yearlyPricePerFoot > 70 && '$'}
                  </abbr>
                )}
              </label>
            ))}
        </div>
      )}
    </React.Fragment>
  );
};

SubMarketNeighborhoodListItem.propTypes = {
  subMarket: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    __typename: PropTypes.string,
    name: PropTypes.string,
    neighborhoods: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        geojson: PropTypes.shape({
          type: PropTypes.string,
          coordinates: PropTypes.arrayOf(
            PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number))
          ),
        }),
        yearlyPricePerFoot: PropTypes.number,
        parameterizedName: PropTypes.string,
      })
    ),
  }),
  onSubMarketChange: PropTypes.func.isRequired,
  onNeighborhoodCheck: PropTypes.func.isRequired,
  selectedNeighborhoods: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      geojson: PropTypes.shape({
        type: PropTypes.string,
        coordinates: PropTypes.arrayOf(
          PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number))
        ),
      }),
      yearlyPricePerFoot: PropTypes.number,
      parameterizedName: PropTypes.string,
    })
  ).isRequired,
  isChecked: PropTypes.bool.isRequired,
};

SubMarketNeighborhoodListItem.defaultProps = {
  subMarket: null,
};

export default SubMarketNeighborhoodListItem;
